import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

const apiService = {
  // Auth
  login: (email: string, password: string) => api.post('/auth/login', { email, password }),
  register: (data: any) => api.post('/auth/register', data),
  getMe: () => api.get('/auth/me'),
  changePassword: (currentPassword: string, newPassword: string) => 
    api.post('/auth/change-password', { currentPassword, newPassword }),

  // Users
  getUsers: () => api.get('/users'),
  getUser: (id: string) => api.get(`/users/${id}`),
  createUser: (data: any) => api.post('/users', data),
  updateUser: (id: string, data: any) => api.put(`/users/${id}`, data),
  deleteUser: (id: string) => api.delete(`/users/${id}`),

  // Customers
  getCustomers: (params?: any) => api.get('/customers', { params }),
  getAllCustomers: () => api.get('/customers/all'),
  getCustomer: (id: string) => api.get(`/customers/${id}`),
  createCustomer: (data: any) => api.post('/customers', data),
  updateCustomer: (id: string, data: any) => api.put(`/customers/${id}`, data),
  deleteCustomer: (id: string) => api.delete(`/customers/${id}`),

  // Quotation Items
  getQuotationItems: (params?: any) => api.get('/quotation-items', { params }),
  getAllQuotationItems: () => api.get('/quotation-items/all'),
  getQuotationItem: (id: string) => api.get(`/quotation-items/${id}`),
  createQuotationItem: (data: any) => api.post('/quotation-items', data),
  updateQuotationItem: (id: string, data: any) => api.put(`/quotation-items/${id}`, data),
  deleteQuotationItem: (id: string) => api.delete(`/quotation-items/${id}`),
  importQuotationItemsFromExcel: (file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/quotation-items/import-excel', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  downloadQuotationItemsSampleExcel: () => {
    return api.get('/quotation-items/sample-excel', {
      responseType: 'blob',
    });
  },

  // Jobs
  getJobs: (params?: any) => api.get('/jobs', { params }),
  getAllJobs: () => api.get('/jobs/all'),
  getJob: (id: string) => api.get(`/jobs/${id}`),
  createJob: (data: any) => api.post('/jobs', data),
  updateJob: (id: string, data: any) => api.put(`/jobs/${id}`, data),
  deleteJob: (id: string) => api.delete(`/jobs/${id}`),

  // Quotations
  getQuotations: (params?: any) => api.get('/quotations', { params }),
  getAllQuotations: () => api.get('/quotations/all'),
  getQuotation: (id: string) => api.get(`/quotations/${id}`),
  createQuotation: (data: any) => api.post('/quotations', data),
  updateQuotation: (id: string, data: any) => api.put(`/quotations/${id}`, data),
  updateQuotationStatus: (id: string, status: string) => api.patch(`/quotations/${id}/status`, { status }),
  deleteQuotation: (id: string) => api.delete(`/quotations/${id}`),
  duplicateQuotation: (id: string) => api.post(`/quotations/${id}/duplicate`),
  reviseQuotation: (id: string, revisionNumber: number) => api.post(`/quotations/${id}/revise`, { revisionNumber }),

  // BOQs
  getBOQs: (params?: any) => api.get('/boqs', { params }),
  getAllBOQs: () => api.get('/boqs/all'),
  getBOQ: (id: string) => api.get(`/boqs/${id}`),
  createBOQ: (data: any) => api.post('/boqs', data),
  updateBOQ: (id: string, data: any) => api.put(`/boqs/${id}`, data),
  updateBOQStatus: (id: string, status: string) => api.patch(`/boqs/${id}/status`, { status }),
  deleteBOQ: (id: string) => api.delete(`/boqs/${id}`),
  duplicateBOQ: (id: string) => api.post(`/boqs/${id}/duplicate`),
  reviseBOQ: (id: string, revisionNumber: number) => api.post(`/boqs/${id}/revise`, { revisionNumber }),

  // Notifications
  getNotifications: (params?: { limit?: number; unreadOnly?: string }) =>
    api.get('/notifications', { params }),
  markNotificationRead: (id: string) => api.patch(`/notifications/${id}/read`),
  markAllNotificationsRead: () => api.patch('/notifications/read-all'),

  // Audit / History
  getAuditLogs: (params: { entityType: 'quotation' | 'boq'; entityId?: string }) =>
    api.get('/audit-logs', { params }),

  // Settings
  getSettings: () => api.get('/settings'),
  updateSettings: (data: any) => api.put('/settings', data),

  // Email
  sendQuotation: (id: string, data: any) => api.post(`/email/send-quotation/${id}`, data),

  // Schedules
  getSchedules: () => api.get('/schedules'),
  getSchedule: (id: string) => api.get(`/schedules/${id}`),
  createSchedule: (data: any) => api.post('/schedules', data),
  updateSchedule: (id: string, data: any) => api.put(`/schedules/${id}`, data),
  deleteSchedule: (id: string) => api.delete(`/schedules/${id}`),

  // Sections
  getSections: () => api.get('/sections'),
  getSection: (id: string) => api.get(`/sections/${id}`),
  createSection: (data: any) => api.post('/sections', data),
  updateSection: (id: string, data: any) => api.put(`/sections/${id}`, data),
  deleteSection: (id: string) => api.delete(`/sections/${id}`),

  // Categories
  getCategories: () => api.get('/categories'),
  getCategory: (id: string) => api.get(`/categories/${id}`),
  createCategory: (data: any) => api.post('/categories', data),
  updateCategory: (id: string, data: any) => api.put(`/categories/${id}`, data),
  deleteCategory: (id: string) => api.delete(`/categories/${id}`),

  // Brands
  getBrands: () => api.get('/brands'),
  getBrand: (id: string) => api.get(`/brands/${id}`),
  createBrand: (data: any) => api.post('/brands', data),
  updateBrand: (id: string, data: any) => api.put(`/brands/${id}`, data),
  deleteBrand: (id: string) => api.delete(`/brands/${id}`),

  // Types
  getTypes: () => api.get('/types'),
  getType: (id: string) => api.get(`/types/${id}`),
  createType: (data: any) => api.post('/types', data),
  updateType: (id: string, data: any) => api.put(`/types/${id}`, data),
  deleteType: (id: string) => api.delete(`/types/${id}`),

  // Compressor Types
  getCompressorTypes: () => api.get('/compressor-types'),
  getCompressorType: (id: string) => api.get(`/compressor-types/${id}`),
  createCompressorType: (data: any) => api.post('/compressor-types', data),
  updateCompressorType: (id: string, data: any) => api.put(`/compressor-types/${id}`, data),
  deleteCompressorType: (id: string) => api.delete(`/compressor-types/${id}`),

  // Countries
  getCountries: () => api.get('/countries'),
  getCountry: (id: string) => api.get(`/countries/${id}`),
  createCountry: (data: any) => api.post('/countries', data),
  updateCountry: (id: string, data: any) => api.put(`/countries/${id}`, data),
  deleteCountry: (id: string) => api.delete(`/countries/${id}`),

  // Units
  getUnits: () => api.get('/units'),
  getUnit: (id: string) => api.get(`/units/${id}`),
  createUnit: (data: any) => api.post('/units', data),
  updateUnit: (id: string, data: any) => api.put(`/units/${id}`, data),
  deleteUnit: (id: string) => api.delete(`/units/${id}`),

  // Terms and Conditions
  getTermsAndConditions: (params?: any) => api.get('/terms-and-conditions', { params }),
  getTermsAndCondition: (id: string) => api.get(`/terms-and-conditions/${id}`),
  createTermsAndCondition: (data: any) => api.post('/terms-and-conditions', data),
  updateTermsAndCondition: (id: string, data: any) => api.put(`/terms-and-conditions/${id}`, data),
  deleteTermsAndCondition: (id: string) => api.delete(`/terms-and-conditions/${id}`),

  // Notes
  getNotes: (params?: any) => api.get('/notes', { params }),
  getNote: (id: string) => api.get(`/notes/${id}`),
  createNote: (data: any) => api.post('/notes', data),
  updateNote: (id: string, data: any) => api.put(`/notes/${id}`, data),
  deleteNote: (id: string) => api.delete(`/notes/${id}`),

  // Footnotes
  getFootnotes: (params?: any) => api.get('/footnotes', { params }),
  getFootnote: (id: string) => api.get(`/footnotes/${id}`),
  createFootnote: (data: any) => api.post('/footnotes', data),
  updateFootnote: (id: string, data: any) => api.put(`/footnotes/${id}`, data),
  deleteFootnote: (id: string) => api.delete(`/footnotes/${id}`),

  // Employees
  getEmployees: () => api.get('/employees'),
  getEmployee: (id: string) => api.get(`/employees/${id}`),
  createEmployee: (data: any) => api.post('/employees', data),
  updateEmployee: (id: string, data: any) => api.put(`/employees/${id}`, data),
  deleteEmployee: (id: string) => api.delete(`/employees/${id}`),

  // Vats
  getVats: () => api.get('/vats'),
  getVat: (id: string) => api.get(`/vats/${id}`),
  createVat: (data: any) => api.post('/vats', data),
  updateVat: (id: string, data: any) => api.put(`/vats/${id}`, data),
  deleteVat: (id: string) => api.delete(`/vats/${id}`),

  // AITs
  getAITs: () => api.get('/aits'),
  getAIT: (id: string) => api.get(`/aits/${id}`),
  createAIT: (data: any) => api.post('/aits', data),
  updateAIT: (id: string, data: any) => api.put(`/aits/${id}`, data),
  deleteAIT: (id: string) => api.delete(`/aits/${id}`),

  // Profit Margins
  getProfitMargins: () => api.get('/profit-margins'),
  getProfitMargin: (id: string) => api.get(`/profit-margins/${id}`),
  createProfitMargin: (data: any) => api.post('/profit-margins', data),
  updateProfitMargin: (id: string, data: any) => api.put(`/profit-margins/${id}`, data),
  deleteProfitMargin: (id: string) => api.delete(`/profit-margins/${id}`),

  // Customs Duties
  getCustomsDuties: () => api.get('/customs-duties'),
  getCustomsDuty: (id: string) => api.get(`/customs-duties/${id}`),
  createCustomsDuty: (data: any) => api.post('/customs-duties', data),
  updateCustomsDuty: (id: string, data: any) => api.put(`/customs-duties/${id}`, data),
  deleteCustomsDuty: (id: string) => api.delete(`/customs-duties/${id}`),

  // Generic methods
  get: (url: string, config?: any) => api.get(url, config),
  post: (url: string, data?: any, config?: any) => api.post(url, data, config),
  put: (url: string, data?: any, config?: any) => api.put(url, data, config),
  delete: (url: string, config?: any) => api.delete(url, config),
  patch: (url: string, data?: any, config?: any) => api.patch(url, data, config),
};

export default apiService;

